import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { fetchTopFolders } from '../../../../flux/actions/apsThunks';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { FolderTreeNode } from '../FolderTreeNode/FolderTreeNode';
import './ModelPicker.css';

/**
 * The ACC folder tree — the "Add models" half of the group editor. Browsing
 * is the whole picker: an admin already knows ACC's folder layout, and that
 * context is worth more here than a search box would be.
 */
export const ModelPicker = ({
    projectId,
    otherGroupsByUrn,
    selectedUrns,
    onToggleModel,
    disabled,
}) => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const folders = useAppSelector(state => state.projects.aps[projectId]?.folders);
    const requestedRef = useRef(false);
    const [expanded, setExpanded] = useState(() => new Set());

    useEffect(() => {
        if (!folders || folders.top.data.length > 0 || folders.top.isLoading || folders.top.error) return;
        if (requestedRef.current) return;
        requestedRef.current = true;
        (async () => {
            try {
                const accessToken = await getAccessToken();
                if (accessToken) dispatch(fetchTopFolders({ accessToken, projectId }));
            } catch (error) {
                console.error('Failed to acquire API token for the ACC folder tree:', error);
            }
        })();
    }, [folders, dispatch, getAccessToken, projectId]);

    const toggleExpand = (folderUrn) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(folderUrn)) next.delete(folderUrn);
            else next.add(folderUrn);
            return next;
        });
    };

    const rowProps = { selectedUrns, onToggleModel, otherGroupsByUrn, disabled };

    if (!folders) return null;

    return (
        <div className="mg-picker">
            {folders.top.isLoading && folders.top.data.length === 0 && (
                <p className="pd-note">Loading the ACC folder tree…</p>
            )}
            {folders.top.error && <p className="org-error">Could not load the ACC folder tree.</p>}
            {folders.top.data.length === 0 && !folders.top.isLoading && !folders.top.error && (
                <p className="pd-note">No folders found in the connected ACC project.</p>
            )}
            {folders.top.data.map(folder => (
                <FolderTreeNode
                    key={folder.id}
                    projectId={projectId}
                    folderUrn={folder.id}
                    depth={0}
                    expandedSet={expanded}
                    onToggleExpand={toggleExpand}
                    {...rowProps}
                />
            ))}
        </div>
    );
};
